import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { qualityAPI, Concession, ConcessionDashboardStats, ConcessionDeviationType, ConcessionRiskLevel } from '../../lib/api';
import { usePagination } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useIsCustomer, useIsSupplier } from '../../hooks/usePortalMode';
import { useView } from '../../contexts/ViewContext';
import { FIELD_MAPPING } from './constants';

export interface NewConcessionForm {
  supplier: string;
  part_number: string;
  part_description: string;
  quantity_affected: string;
  deviation_type: 'dimensional' | 'cosmetic' | 'material' | 'process' | 'documentation' | 'packaging' | 'other';
  title: string;
  description: string;
  reason: string;
  proposed_disposition: string;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  risk_justification: string;
}

const INITIAL_FORM: NewConcessionForm = {
  supplier: '',
  part_number: '',
  part_description: '',
  quantity_affected: '',
  deviation_type: 'dimensional',
  title: '',
  description: '',
  reason: '',
  proposed_disposition: '',
  risk_level: 'medium',
  risk_justification: '',
};

export function useConcessions() {
  const navigate = useNavigate();
  const isCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();
  const { viewKey, getViewQueryParams } = useView();
  const [stats, setStats] = useState<ConcessionDashboardStats | null>(null);
  const [concessions, setConcessions] = useState<Concession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('requested_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newConcession, setNewConcession] = useState<NewConcessionForm>(INITIAL_FORM);

  const pagination = usePagination({
    pageSize: 15,
    onPageChange: () => {},
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    pagination.reset();
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const backendField = FIELD_MAPPING[sortField] || sortField;
      const ordering = sortDirection === 'desc' ? `-${backendField}` : backendField;

      const viewParams = getViewQueryParams();
      const statsParams: Record<string, string | number> = {};
      if (viewParams['site']) statsParams['site'] = viewParams['site'];
      if (viewParams['company']) statsParams['company'] = viewParams['company'];
      if (viewParams['group']) statsParams['group'] = viewParams['group'];
      const [statsResponse, concessionsResponse] = await Promise.all([
        qualityAPI.getConcessionStats(statsParams),
        qualityAPI.getConcessions({
          ordering,
          page: pagination.currentPage,
          page_size: pagination.pageSize,
          search: searchTerm || undefined,
          ...viewParams,
        }),
      ]);

      setStats(statsResponse.data);
      setConcessions(concessionsResponse.data.results);
      pagination.setFromResponse(concessionsResponse.data.count);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load concessions data'));
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, searchTerm, sortField, sortDirection, isCustomer, viewKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewConcession = (concessionId: number) => {
    navigate(`/quality/concession/${concessionId}`);
  };

  const handleCreateConcession = async () => {
    try {
      setCreateLoading(true);
      await qualityAPI.createConcession({
        supplier: parseInt(newConcession.supplier),
        part_number: newConcession.part_number,
        part_description: newConcession.part_description,
        quantity_affected: parseInt(newConcession.quantity_affected),
        deviation_type: newConcession.deviation_type as ConcessionDeviationType,
        title: newConcession.title,
        description: newConcession.description,
        reason: newConcession.reason,
        proposed_disposition: newConcession.proposed_disposition,
        risk_level: newConcession.risk_level as ConcessionRiskLevel,
        risk_justification: newConcession.risk_justification,
      });
      setShowCreateDialog(false);
      setNewConcession(INITIAL_FORM);
      fetchData();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create concession'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    pagination.reset();
  };

  return {
    // Navigation
    navigate,

    // User info
    isCustomer,
    isSupplier,

    // Loading states
    loading,
    error,
    createLoading,

    // Data
    stats,
    concessions,
    searchTerm,
    sortField,
    sortDirection,
    pagination,

    // Create dialog
    showCreateDialog,
    setShowCreateDialog,
    newConcession,
    setNewConcession,

    // Actions
    fetchData,
    handleSort,
    handleViewConcession,
    handleCreateConcession,
    handleSearchChange,
  };
}
